/**
 * Tracks whether field writes are getting through, so the UI can stop claiming "Saved" when they are not.
 *
 * The app's `online` mode (useOffline) is decided once at startup and deliberately not flipped mid-session, because it
 * changes where writes go. This module is the lighter signal that sits next to it: every field write reports its result
 * here, the browser's own online/offline events are watched, and fields that failed to save register a resubmit
 * function so their latest value is written again once writes are getting through.
 *
 * Nothing here persists anything; it only remembers, retries, and notifies.
 */

type Listener = () => void;
type Resubmit = () => Promise<boolean>;

interface ConnectionState {
	/** navigator.onLine as last reported by the browser */
	browserOnline: boolean;
	/** Consecutive failed writes across all fields; reset by any success */
	consecutiveFailures: number;
	/** Field ids with a write in flight */
	pending: Set<string>;
	/** Field ids whose last write failed, with a function that writes their current value again */
	failed: Map<string, Resubmit>;
}

/** After this many consecutive failed writes the connection is treated as degraded even if the browser says it is online. */
const FAILURE_STREAK_THRESHOLD = 2;
/** Backoff (ms) between automatic resubmit attempts while writes keep failing. */
const RETRY_DELAYS_MS = [2000, 5000, 15000, 30000];

const state: ConnectionState = {
	browserOnline: typeof navigator === "undefined" ? true : navigator.onLine,
	consecutiveFailures: 0,
	pending: new Set(),
	failed: new Map(),
};

const listeners = new Set<Listener>();
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryAttempt = 0;
let retryInFlight = false;

function notify() {
	listeners.forEach((listener) => {
		try {
			listener();
		} catch {
			// A listener failing must not break the others
		}
	});
}

/** Subscribe to changes. Returns the unsubscribe function. */
export function subscribeToConnectionStatus(listener: Listener): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/** True when the browser reports offline or writes keep failing. */
export function isConnectionDegraded(): boolean {
	return !state.browserOnline || state.consecutiveFailures >= FAILURE_STREAK_THRESHOLD;
}

/** True when any field has a write in flight or a write that failed and has not been resubmitted successfully. */
export function hasUnsavedWork(): boolean {
	return state.pending.size > 0 || state.failed.size > 0;
}

/** Number of fields whose last write failed. */
export function failedWriteCount(): number {
	return state.failed.size;
}

/** Marks a field write as started. */
export function markWritePending(fieldId: string): void {
	state.pending.add(fieldId);
	notify();
}

/**
 * Records the outcome of a field write.
 * @param fieldId The field that was written
 * @param ok Whether the write succeeded
 * @param resubmit For failures: writes the field's current value again. Called automatically with backoff and when the
 * browser comes back online.
 */
export function reportWriteResult(fieldId: string, ok: boolean, resubmit?: Resubmit): void {
	state.pending.delete(fieldId);
	if (ok) {
		state.consecutiveFailures = 0;
		state.failed.delete(fieldId);
		if (state.failed.size === 0) {
			retryAttempt = 0;
			if (retryTimer) {
				clearTimeout(retryTimer);
				retryTimer = null;
			}
		}
	} else {
		state.consecutiveFailures++;
		if (resubmit) state.failed.set(fieldId, resubmit);
		scheduleRetry();
	}
	notify();
}

/** Forgets a field entirely (on unmount). Its failed write, if any, can no longer be resubmitted. */
export function forgetField(fieldId: string): void {
	state.pending.delete(fieldId);
	state.failed.delete(fieldId);
	notify();
}

function scheduleRetry() {
	if (retryTimer || state.failed.size === 0) return;
	const delay = RETRY_DELAYS_MS[Math.min(retryAttempt, RETRY_DELAYS_MS.length - 1)];
	retryTimer = setTimeout(() => {
		retryTimer = null;
		retryAttempt++;
		void retryFailedWrites();
	}, delay);
}

/**
 * Writes the current value of every field whose last write failed. Successes clear the field; failures keep it queued
 * and schedule another attempt with a longer delay.
 */
export async function retryFailedWrites(): Promise<void> {
	if (retryInFlight || state.failed.size === 0) return;
	retryInFlight = true;
	try {
		const entries = Array.from(state.failed.entries());
		for (const [fieldId, resubmit] of entries) {
			let ok = false;
			try {
				ok = await resubmit();
			} catch {
				ok = false;
			}
			state.pending.delete(fieldId);
			if (ok) {
				state.failed.delete(fieldId);
				state.consecutiveFailures = 0;
			} else {
				state.consecutiveFailures++;
			}
		}
	} finally {
		retryInFlight = false;
		if (state.failed.size === 0) {
			retryAttempt = 0;
		} else {
			scheduleRetry();
		}
		notify();
	}
}

/** Test/support helper: clears everything. */
export function resetConnectionStatus(): void {
	state.browserOnline = typeof navigator === "undefined" ? true : navigator.onLine;
	state.consecutiveFailures = 0;
	state.pending.clear();
	state.failed.clear();
	retryAttempt = 0;
	if (retryTimer) {
		clearTimeout(retryTimer);
		retryTimer = null;
	}
	notify();
}

if (typeof window !== "undefined") {
	window.addEventListener("online", () => {
		state.browserOnline = true;
		retryAttempt = 0;
		notify();
		void retryFailedWrites();
	});
	window.addEventListener("offline", () => {
		state.browserOnline = false;
		notify();
	});
	// A reload or navigation would discard the values held in memory for fields that have not been saved yet.
	window.addEventListener("beforeunload", (event) => {
		if (hasUnsavedWork()) {
			event.preventDefault();
			// Legacy browsers need returnValue set to show the prompt
			event.returnValue = "";
		}
	});
}
